/**
 * Google Fonts URL utilities.
 *
 * Shared across the app - used by BrandedChakraProvider (renders <link> tags)
 * and puck-editor utilities (imperative loading for content/editor fonts).
 *
 * ## Font spec format
 *
 * Branding config values can include optional weight specs:
 *   "Rowdies"              → default weights
 *   "Rowdies|300,400,700"  → specific weights
 *   "Oswald|200..700"      → variable font range
 *
 * Plain font names (no pipe) are also accepted everywhere.
 */

/** Curated weight configs for fonts used in the editor font picker */
const FONT_WEIGHTS: Record<string, string> = {
  Inter: "400;500;600;700",
  Poppins: "400;500;600;700",
  Roboto: "400;500;700",
  "Open Sans": "400;500;600;700",
  Lato: "400;700",
  Montserrat: "400;500;600;700",
  Nunito: "400;500;600;700",
  Raleway: "400;500;600;700",
  "Work Sans": "400;500;600;700",
  "DM Sans": "400;500;700",
  "Space Grotesk": "400;500;600;700",
  Manrope: "400;500;600;700;800",
  "Playfair Display": "400;500;600;700",
  Merriweather: "400;700",
  Lora: "400;500;600;700",
  "Libre Baskerville": "400;700",
  "Cormorant Garamond": "400;500;600;700",
  "Source Serif Pro": "400;600;700",
  "Bebas Neue": "400",
  Anton: "400",
  Righteous: "400",
  "Permanent Marker": "400",
  "Archivo Black": "400",
  "JetBrains Mono": "400;500;700",
  "Fira Code": "400;500;700",
  "Source Code Pro": "400;500;700",
};

/** Get all supported font family names (for the editor font picker) */
export function getSupportedFontNames(): string[] {
  return Object.keys(FONT_WEIGHTS);
}

/**
 * Parse a font spec into its name and optional weight parts.
 *
 * Handles Google Fonts format only: "Rowdies" or "Rowdies|300,400,700".
 * For unified resolution of any spec (including Adobe), use getFontFamilyName from font-spec.
 *
 * @example
 * parseFontSpec("Rowdies")             → { name: "Rowdies" }
 * parseFontSpec("Rowdies|300,400,700") → { name: "Rowdies", weights: "300;400;700" }
 * parseFontSpec("Oswald|200..700")     → { name: "Oswald", weights: "200..700" }
 */
export function parseFontSpec(spec: string): {
  name: string;
  weights?: string;
} {
  const pipeIndex = spec.indexOf("|");
  if (pipeIndex === -1) return { name: spec };

  const name = spec.slice(0, pipeIndex);
  const rawWeights = spec.slice(pipeIndex + 1);
  // Comma-separated → semicolons for the CSS2 API (range "200..700" passes through)
  const weights = rawWeights.replace(/,/g, ";");
  return { name, weights };
}

/**
 * Build the `family=` param fragment for a single font spec.
 *
 * Priority: explicit weights from spec > curated map > no weights.
 */
function buildFamilyParam(fontSpec: string): string {
  const { name, weights } = parseFontSpec(fontSpec);
  const encoded = name.replace(/ /g, "+");
  const curated = FONT_WEIGHTS[name];

  let suffix = "";
  if (weights) {
    suffix = `:wght@${weights}`;
  } else if (curated) {
    suffix = `:wght@${curated}`;
  }

  return `family=${encoded}${suffix}`;
}

/**
 * Get the Google Fonts CSS2 URL for a single font.
 * Used by imperative loading (puck editor content fonts).
 *
 * Uses display=block so text is hidden briefly until the font loads,
 * preventing a flash of unstyled text (FOUT).
 */
export function getGoogleFontUrl(fontSpec: string): string {
  return `https://fonts.googleapis.com/css2?${buildFamilyParam(
    fontSpec
  )}&display=block`;
}

/**
 * Build a single Google Fonts CSS2 URL for multiple fonts.
 * Combines all fonts into one request with multiple `family=` params.
 *
 * Uses display=block so text is hidden briefly until the font loads,
 * preventing a flash of unstyled text (FOUT).
 *
 * @example
 * buildGoogleFontsUrl(["Rowdies|300,400,700", "Oswald|200..700"])
 * → "https://fonts.googleapis.com/css2?family=Rowdies:wght@300;400;700&family=Oswald:wght@200..700&display=block"
 */
export function buildGoogleFontsUrl(fontSpecs: string[]): string | null {
  if (fontSpecs.length === 0) return null;
  const params = fontSpecs.map(buildFamilyParam).join("&");
  return `https://fonts.googleapis.com/css2?${params}&display=block`;
}
